/**
 * Coverage facts for /looker (spec §9.3): which platforms spent but did not
 * report a metric, which days are missing between a platform's first and last
 * spend day, and how many of the campaigns that spent have conforming names
 * (§9.2; a count since rules-04 E2, as D2's).
 *
 * A metric is "not reported" when a platform's spending rows carry no non-NULL
 * value for it; a stored 0 is a reported zero and never a gap. The metric
 * columns DEFAULT 0, so a writer that omits a metric cannot be detected. Reach
 * is the known exception, judged by both halves of reachNotSent (rules-04 D6,
 * G2): get_metric_coverage (migration 126) does not count a reach of 0 after
 * impressions, and a platform that sends none (PLATFORMS_WITHOUT_REACH, whose
 * writer stores 0s) is named in box 8 and in the Result gaps under Reach.
 * Analytics rows are excluded everywhere. Pure: no reads, no clock.
 *
 * @module services/looker/lookerCoverage
 */
import { classifyCampaignName } from './campaignNaming';
import { PLATFORMS_WITHOUT_REACH } from '../platformReach';
import type { AwarenessMetric } from '../targets/resultMetric';

export type ResultObjective = 'awareness' | 'consideration' | 'conversion';
export type CoverageMetric = 'impressions' | 'clicks' | 'conversions' | 'reach' | 'engagements' | 'conversion_value';

/**
 * The central Result metric per objective. `retention` has none and is ignored.
 * DEC3: Awareness follows the workspace's mapping (coverageOf's `awareness`); this is its default.
 */
export const RESULT_METRIC: Record<ResultObjective, CoverageMetric> = {
  awareness: 'impressions',
  consideration: 'clicks',
  conversion: 'conversions',
};
const RESULT_OBJECTIVES: readonly ResultObjective[] = ['awareness', 'consideration', 'conversion'];
/** The non-Result metrics box 8 lists, in display order. */
const OTHER_METRICS: readonly CoverageMetric[] = ['reach', 'engagements', 'conversion_value'];

export interface MetricGap { platform: string; metric: CoverageMetric; spend: number }
/** ISO dates, ascending. */
export interface MissingDays { platform: string; days: string[] }
/** Campaigns with spend, by name (rules-04 E2: a count, as D2's): share = conforming ÷ withSpend, null when none spent. */
export interface NamingCoverage { conforming: number; withSpend: number; share: number | null; unclassified: number }
export interface LookerCoverage {
  /** Per (platform, objective): spending rows, none of which carry the Result metric. */
  resultGaps: Record<ResultObjective, MetricGap[]>;
  /** reach / engagements / conversion_value, per platform across all objectives. */
  metricGaps: MetricGap[];
  missingDays: MissingDays[];
  naming: NamingCoverage;
}
/** One get_mmm_coverage row (platform-day). */
export interface McovRow { platform: string; date: string; cost: number | null }

/**
 * One get_metric_coverage row (migration 115; reach_n as migration 126 counts
 * it): per (platform, objective), the rows with cost > 0 and how many of them
 * carry a non-NULL value per metric, a reach of 0 after impressions left out.
 * The campaign summary cannot say this: it COALESCEs every SUM to 0.
 */
export interface MetricCoverageRow {
  platform: string;
  objective: string | null;
  spend: number;
  cost_rows: number;
  impressions_n: number;
  clicks_n: number;
  conversions_n: number;
  reach_n: number;
  engagements_n: number;
  conversion_value_n: number;
}

/** The campaign-summary fields this module reads; a `CampaignSummaryRow` is assignable. */
export interface SummaryRow {
  platform: string;
  campaign_name: string | null;
  total_spend: number | null | undefined;
}

const COUNT_FIELD: Record<CoverageMetric, keyof MetricCoverageRow> = {
  impressions: 'impressions_n',
  clicks: 'clicks_n',
  conversions: 'conversions_n',
  reach: 'reach_n',
  engagements: 'engagements_n',
  conversion_value: 'conversion_value_n',
};

/** bigint / numeric may arrive as strings; NULL counts as 0. */
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};
const spendOf = (rows: readonly SummaryRow[]): number => rows.reduce((s, r) => s + num(r.total_spend), 0);
const sumOf = (rows: readonly MetricCoverageRow[], key: keyof MetricCoverageRow): number =>
  rows.reduce((s, r) => s + num(r[key]), 0);

/** Rows grouped by platform, platforms sorted so outputs are deterministic. */
const byPlatform = <R extends { platform: string }>(rows: readonly R[]): Array<[string, R[]]> => {
  // Built locally and never shared; copying per row would be quadratic.
  const groups = new Map<string, R[]>();
  rows.forEach((r) => {
    const group = groups.get(r.platform);
    if (group) group.push(r);
    else groups.set(r.platform, [r]);
  });
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
};

/** Per platform over `rows`: it had spending rows, and none carried `metric` (or `unsent` says the platform sends none). */
const gapsFor = (
  rows: readonly MetricCoverageRow[],
  metrics: readonly CoverageMetric[],
  unsent: (platform: string, metric: CoverageMetric) => boolean = () => false,
): MetricGap[] =>
  byPlatform(rows).flatMap(([platform, group]) => {
    if (sumOf(group, 'cost_rows') <= 0) return [];
    const spend = sumOf(group, 'spend');
    return metrics
      .filter((metric) => unsent(platform, metric) || sumOf(group, COUNT_FIELD[metric]) === 0)
      .map((metric) => ({ platform, metric, spend }));
  });

/**
 * The platform half of reachNotSent (rules-04 D6, G2): a platform that sends no reach has not reported it, though the
 * RPC still counts the 0 its writer stores on a row with no impressions. The row half, a 0 after impressions, is the
 * RPC's own (migration 126). Box 8 and the Result gaps under a Reach mapping both apply it.
 */
const reachUnsent = (platform: string, metric: CoverageMetric): boolean => metric === 'reach' && PLATFORMS_WITHOUT_REACH.has(platform);

const resultGapsOf = (rows: readonly MetricCoverageRow[], awareness: AwarenessMetric | null): Record<ResultObjective, MetricGap[]> => {
  // null: the definition was not read, so no Awareness metric is checked (gapsFor(rows, []) is []).
  const metricOf = (o: ResultObjective): CoverageMetric[] =>
    o !== 'awareness' ? [RESULT_METRIC[o]] : awareness === null ? [] : [awareness];
  const entries = RESULT_OBJECTIVES.map((o) => [o, gapsFor(rows.filter((r) => r.objective === o), metricOf(o), reachUnsent)] as const);
  return Object.fromEntries(entries) as Record<ResultObjective, MetricGap[]>;
};

const DAY_MS = 86_400_000;
const dayMs = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);
const isoOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Days in [first, last] spend day (inside the period) with no row at all. */
const missingFor = (platform: string, days: readonly McovRow[], period: { start: string; end: string }): MissingDays | null => {
  const own = days.filter((d) => d.platform === platform && d.date >= period.start && d.date <= period.end);
  const spent = own.filter((d) => num(d.cost) > 0).map((d) => d.date).sort();
  if (spent.length === 0) return null;
  const present = new Set(own.map((d) => d.date));
  const [first, last] = [dayMs(spent[0]), dayMs(spent[spent.length - 1])];
  const gaps = Array.from({ length: Math.round((last - first) / DAY_MS) + 1 }, (_, i) => isoOf(first + i * DAY_MS))
    .filter((d) => !present.has(d));
  return gaps.length > 0 ? { platform, days: gaps } : null;
};

const missingDaysOf = (rows: readonly SummaryRow[], days: readonly McovRow[], period: { start: string; end: string }): MissingDays[] =>
  byPlatform(rows)
    .filter(([, group]) => spendOf(group) > 0)
    .flatMap(([platform]) => {
      const m = missingFor(platform, days, period);
      return m ? [m] : [];
    });

/** Proposal D2.4 (rules-04 E2): conforming campaigns ÷ campaigns with spend > 0. A count adds no money, so any currency. */
const namingOf = (rows: readonly SummaryRow[]): NamingCoverage => {
  const spending = rows.filter((r) => num(r.total_spend) > 0);
  const conforming = spending.filter((r) => classifyCampaignName(r.campaign_name).conforms).length;
  const withSpend = spending.length;
  return { conforming, withSpend, share: withSpend > 0 ? conforming / withSpend : null, unclassified: withSpend - conforming };
};

/**
 * The naming coverage as box 8 prints it, on /overview and /looker, and as D2's box 5 does (rules-04 E2): floored to
 * 0.1, never rounded, so 100.0% means every campaign conforms; null (no campaign spent) is "—". It lives here because
 * services/looker imports nothing from services/overview (rules-04 D review M7).
 */
export const fmtNamingCoverage = (share: number | null): string =>
  (share === null ? '—' : `${(Math.floor(share * 1000 + 1e-9) / 10).toFixed(1)}%`);

/**
 * Every coverage fact for one workspace and a bounded period (spec §9.3).
 * Gaps come from get_metric_coverage; missing days from get_mmm_coverage; the
 * naming count and "had spend" for missing days from the campaign summary.
 */
export const coverageOf = (
  summary: readonly SummaryRow[],
  metrics: readonly MetricCoverageRow[],
  days: readonly McovRow[],
  period: { start: string; end: string },
  /** DEC3: the Awareness Result metric in force; null = unread (no Awareness gap). Default = the central definition. */
  awareness: AwarenessMetric | null = 'impressions',
): LookerCoverage => {
  const media = summary.filter((r) => r.platform !== 'analytics');
  const mediaMetrics = metrics.filter((r) => r.platform !== 'analytics');
  const mediaDays = days.filter((d) => d.platform !== 'analytics');
  return {
    resultGaps: resultGapsOf(mediaMetrics, awareness),
    metricGaps: gapsFor(mediaMetrics, OTHER_METRICS, reachUnsent),
    missingDays: missingDaysOf(media, mediaDays, period),
    naming: namingOf(media),
  };
};
